// The world's floor plans: every walkable area (the planet surface, each
// club) is an AreaPlan — bounds, obstacles, portals, spawn, booth. Movement
// collides with it, the server simulates it, the renderer dresses it. One
// source of truth so what you see is exactly what you collide with.
//
// The surface is the planet: 16.4 km of open terrain with the settlement's
// street furniture at the origin plateau. The settlement keeps the old
// street layout exactly — same facades, same doors, same planters — but the
// perimeter walls are gone and the world continues.

import { CITY_HALF } from "./city-terrain";
import type { Aabb } from "./constants";
import { PLANET_HALF } from "./terrain";
import type { WorldId } from "./worlds";

export interface PortalDef {
  /** Stable id within the room (e.g. "door-club-zero"). */
  id: string;
  /** Trigger area in THIS room; walking into it starts the transition. */
  zone: Aabb;
  /** Room this portal leads to. */
  target: string;
  /** Arrival pose in the TARGET room (must not overlap its portals). */
  arrival: { x: number; z: number; yaw: number };
  /** Sign text rendered over the door (settlement side). */
  label?: string;
}

export interface AreaPlan {
  id: string;
  label: string;
  /**
   * What kind of place this is. Drives the state class on the wire, the
   * dressing, and which verbs the room accepts — a workshop is an interior
   * like a club but has no booth, no mount, and no residency to offer.
   * "space" is the Black: no walking, no portals, ships only. "home" is a
   * player-raised interior; its plans are synthesized from the room id
   * (shared/housing.ts) rather than listed in PLANS. "district" is an
   * outdoor-feeling walkable quarter that is nevertheless its own room —
   * the Understreet needs its own sky, fog and luminance regime, which is
   * exactly what a room boundary already provides.
   */
  kind: "surface" | "club" | "workshop" | "clinic" | "space" | "home" | "district";
  /**
   * Which planet this room stands on. Surface rooms use it to pick their
   * heightfield and sky; interiors inherit it for flavor only. Absent on
   * the Black (space belongs to no world) and defaults to the frontier
   * everywhere legacy code asks.
   */
  world?: WorldId;
  /**
   * Whether this ground yields and bites: the gathering verbs (survey,
   * sample) work only where wilds is true, and the Edge accumulates
   * exposure only there. Meridian is paved and drained — a body recovers
   * there the way it does indoors, and the aetherometer reads nothing
   * worth sealing. The frontier surface is the only wilds today.
   */
  wilds?: boolean;
  /** Half-extent of the walkable box along x / z, in metres. */
  halfWidth: number;
  halfDepth: number;
  wallHeight: number;
  obstacles: readonly Aabb[];
  portals: readonly PortalDef[];
  /** Where characters new to this room appear (scattered by radius). */
  spawn: { x: number; z: number; radius: number; yaw: number };
  /** Icecast mount id (mount path is /<mount>); absent = no music. */
  mount?: string;
  /** The DJ booth footprint; clubs only. Also the audio anchor. */
  booth?: Aabb;
  /**
   * The workbench footprint; workshops only. Solid, dressed by the
   * renderer, and the reason the room exists — "being at the bench" means
   * being in a room that has one.
   */
  bench?: Aabb;
  /**
   * The other work stations in a workshop — the luthier's rack, the
   * tailor's table, the joiner's bench, the copper still. Solid, dressed
   * by the renderer, and carried into provenance as bench identity VALUES
   * (crafting v2 decision 17): a second workshop needs no migration, and
   * "being at the bench" stays a room fact, not a footprint fact.
   */
  stations?: readonly { id: string; label: string; box: Aabb }[];
  /**
   * Where the night's flyers sit, by the door; clubs only. Somebody who
   * was there takes one from here rather than from a menu.
   */
  flyerStack?: { x: number; z: number };
  /** Proximity chat radius in metres; absent = room-wide chat. */
  chatRadius?: number;
  /** Interior accent color (lights, strip, signage). */
  accent: number;
}

/**
 * The planet room id. The pre-planet street room was "street"; migration
 * 0002 moved every character over with coordinates preserved (the
 * settlement keeps the street layout at the origin).
 */
export const SURFACE_ID = "surface";
export const CLUB_ZERO_ID = "club-zero";

/**
 * The Black — the space over the planet, as its own room. Deliberately a
 * dedicated lightweight scene (starfield, nebula, the planet as a lit
 * sphere below) rather than any attempt at orbit↔surface streaming: the
 * fidelity audit's transition-grammar ruling. Big and sparse; interest
 * management reuses the surface's machinery with wider radii.
 */
export const SPACE_ID = "space";

/**
 * The spaceport pad: a clearing on the plateau southeast of the street,
 * where the found ships stand and where a descent sets you down. A modest
 * pad — the spaceport district is a later visual piece.
 */
export const SPACEPORT_PAD = { x: 64, z: 78, radius: 22 } as const;

// ---------------------------------------------------------------------------
// Meridian — the port capital. The frontier planet is where things are dug
// and made; Meridian is where they are sold, shipped and danced to
// (roadmap H2: "a mining town to the capital's port city"). One surface
// room on the city crown, one always-night district decked over beneath
// it, and two venues on the existing club rails.

/** Meridian's surface room: the crown — the Prom, the port, the terraces. */
export const MERIDIAN_ID = "meridian";
/**
 * The Understreet: the old street level of Meridian, decked over when the
 * terraces were raised above it. It has not seen the sky in a century —
 * its perpetual night is architecture, not weather — which is why it is
 * its own room: a night-city luminance regime needs its own fog and its
 * own dark.
 */
export const UNDERSTREET_ID = "understreet";
/** The market-terrace venue, on the Prom's north row. */
export const COUNTERWEIGHT_ID = "counterweight";
/** The Understreet venue. The scene travels. */
export const UNDERTOW_ID = "undertow";

/** Meridian's landing apron, east of the Prom. Descents aim here. */
export const CITY_PAD = { x: 240, z: 0, radius: 30 } as const;

/** The Prom: Meridian's main avenue, east–west across the crown. */
export const PROM = {
  /** Walkable street runs z ∈ [−halfDepth, halfDepth]. */
  halfDepth: 9,
  /** From the Understreet gate in the west to the port apron in the east. */
  minX: -210,
  maxX: 205,
} as const;

/** North-row building centres along the Prom (buildings face +z). */
export const PROM_NORTH_ROW: readonly { x: number; width: number; venue?: string }[] = [
  { x: -150, width: 12 },
  { x: -110, width: 14 },
  { x: -60, width: 12, venue: COUNTERWEIGHT_ID },
  { x: -10, width: 10 },
  { x: 40, width: 14 },
  { x: 90, width: 12 },
  { x: 140, width: 10 },
];

/** South-row building centres (buildings face −z). */
export const PROM_SOUTH_ROW: readonly { x: number; width: number }[] = [
  { x: -130, width: 12 },
  { x: -35, width: 14 },
  { x: 15, width: 10 },
  { x: 65, width: 12 },
  { x: 115, width: 14 },
  { x: 155, width: 10 },
];

/** Building row depth, metres (footprints are solid; doors are insets). */
export const PROM_ROW_DEPTH = 17;

/** Meridian's poster wall, on the avenue's south edge by the market row. */
export const CITY_POSTER_WALL: Aabb = { minX: -88.2, maxX: -81.8, minZ: 7.4, maxZ: 8 };

/** The Understreet gate on the crown: a ramp mouth at the Prom's west end. */
export const UNDERSTREET_GATE = {
  zone: { minX: -214, maxX: -206, minZ: -4, maxZ: 4 } as Aabb,
  /** Arriving DOWN: the canyon's east end, facing west into the lights. */
  arrivalBelow: { x: 116, z: 0, yaw: -Math.PI / 2 },
  /** Arriving UP: just inside the crown gate, facing east up the Prom. */
  arrivalAbove: { x: -200, z: 0, yaw: Math.PI / 2 },
} as const;

/** The Understreet's canyon box: a street 264 m long under the decks. */
export const UNDERSTREET_HALF_W = 132;
export const UNDERSTREET_HALF_D = 30;
/**
 * The workshop off the settlement street: one bench, public, unowned.
 *
 * Deliberately a room rather than a fixture on the surface (crafting.md
 * decision 6). It invents no new primitive — portals, collision and
 * interior dressing all already work — and it leaves later bench
 * ownership on a rail that already shipped, because venue_claims.clubId is
 * bare text and /claim passes any non-surface plan id straight through.
 */
export const WORKSHOP_ID = "workshop";

// ---------------------------------------------------------------------------
// Club Zero — the real venue (geometry unchanged from M0/M1).

const CLUB_ZERO_HALF_W = 12;
const CLUB_ZERO_HALF_D = 9;

const CLUB_ZERO_BOOTH: Aabb = {
  minX: -2.2,
  maxX: 2.2,
  minZ: -CLUB_ZERO_HALF_D,
  maxZ: -CLUB_ZERO_HALF_D + 2.2,
};

const CLUB_ZERO_SPEAKERS: readonly Aabb[] = [
  { minX: -5.2, maxX: -3.4, minZ: -CLUB_ZERO_HALF_D, maxZ: -CLUB_ZERO_HALF_D + 1.4 },
  { minX: 3.4, maxX: 5.2, minZ: -CLUB_ZERO_HALF_D, maxZ: -CLUB_ZERO_HALF_D + 1.4 },
];

// ---------------------------------------------------------------------------
// The settlement — the old street, now a plateau town. Clubs line the north
// row (z = -SETTLEMENT.halfDepth). You spawn at the west end facing east, so
// the north row is on your left: the first club on the left is Club Zero.

/**
 * The settlement's street-furniture footprint in surface coordinates.
 * Renderers dress this box; movement authority is the surface plan (whose
 * bounds are the whole planet).
 */
export const SETTLEMENT = {
  halfWidth: 30,
  halfDepth: 8,
  wallHeight: 7,
} as const;

/** Facade centre x-positions along the north row, west to east. */
export const FACADES = [
  { clubId: "club-zero", x: -14 },
  { clubId: "velvet-orbit", x: 2 },
  { clubId: "brass-lantern", x: 18 },
] as const;

const WORKSHOP_HALF_W = 8;
const WORKSHOP_HALF_D = 6;
/** West end of the north row, the last building before the street ends. */
const WORKSHOP_STREET_X = -26;

/**
 * The infirmary off the settlement street: cots, public, unowned.
 *
 * It exists because the Edge made the planet cost something and "the
 * settlement" was the only answer — and the settlement is a 500 m disc of
 * flat ground, which is a fact rather than a place. A body that walked
 * home cold needs somewhere to walk home TO. Public and unowned like the
 * workshop and unlike a venue: a clinic you can be locked out of fails
 * the person it exists for.
 */
export const CLINIC_ID = "clinic";

const CLINIC_HALF_W = 8;
const CLINIC_HALF_D = 6.5;
/** East end of the row, the far side of the clubs from the workshop. */
const CLINIC_STREET_X = 30;

/**
 * Every door on the street, and where you land on the other side. The
 * clubs plus the workshop: the same machinery dresses all of them, which
 * is exactly why the workshop cost no new primitive.
 */
const STREET_DOORS: readonly { target: string; x: number; insideHalfDepth: number }[] = [
  ...FACADES.map(({ clubId, x }) => ({
    target: clubId,
    x,
    insideHalfDepth: clubId === CLUB_ZERO_ID ? CLUB_ZERO_HALF_D : 7,
  })),
  { target: WORKSHOP_ID, x: WORKSHOP_STREET_X, insideHalfDepth: WORKSHOP_HALF_D },
  { target: CLINIC_ID, x: CLINIC_STREET_X, insideHalfDepth: CLINIC_HALF_D },
];

/** Each club facade is a real building: 10 m wide, 4 m deep, solid. */
export function facadeFootprint(x: number): Aabb {
  return { minX: x - 5, maxX: x + 5, minZ: -SETTLEMENT.halfDepth - 4, maxZ: -SETTLEMENT.halfDepth };
}

/**
 * The back row: low utility blocks south of the street (the old skyline
 * backdrop, now solid buildings). Spaced so every pair leaves a walkable
 * alley — the settlement must never read as walled in.
 */
export const BACK_ROW: readonly { x: number; width: number; height: number; box: Aabb }[] =
  Array.from({ length: 6 }, (_, i) => {
    const height = 3 + ((i * 37) % 5);
    const width = 5 + ((i * 13) % 3);
    const x = -SETTLEMENT.halfWidth + 5 + i * 10;
    return {
      x,
      width,
      height,
      box: {
        minX: x - width / 2,
        maxX: x + width / 2,
        minZ: SETTLEMENT.halfDepth + 1.2,
        maxZ: SETTLEMENT.halfDepth + 3.2,
      },
    };
  });

/**
 * The poster wall: a real board on the south side of the street, facing
 * the club fronts. Nights were advertised only in a DOM overlay on the N
 * key, which made "printed and hung" a fiction the world did not carry —
 * a flyer is an object and it needs somewhere to be.
 *
 * Solid, so you walk up to it rather than through it, and clear of the
 * spawn, the doors and the skiff line.
 */
export const POSTER_WALL: Aabb = { minX: -3.2, maxX: 3.2, minZ: 6.2, maxZ: 6.8 };
export const POSTER_WALL_HEIGHT = 3.2;

/**
 * The offer board: the Merchant's market, a physical board in the market
 * row — the stretch of the south side in front of the back-row utility
 * blocks (trades.md §19). A place, not a global UI: you read it standing
 * in front of it, in the locker's spirit. Well inside the sixty-metre
 * locker radius so "check the board" and "empty the hold" are one errand.
 */
export const MARKET_BOARD: Aabb = { minX: 12.8, maxX: 17.2, minZ: 6.2, maxZ: 6.8 };
export const MARKET_BOARD_HEIGHT = 2.8;
/** How close you must stand to post, fill or withdraw a lot. */
export const MARKET_BOARD_RANGE_M = 8;

/**
 * The Job board: the settlement's recorded asks (trades.md §14–§16), on
 * the west stretch of the same south side. One object on the surface, not
 * a new interior: a new room costs five branch points and buys nothing
 * the street does not give.
 */
export const JOB_BOARD: Aabb = { minX: -17.2, maxX: -12.8, minZ: 6.2, maxZ: 6.8 };
export const JOB_BOARD_HEIGHT = 2.8;
/** How close you must stand to post, take or deliver a job. */
export const JOB_BOARD_RANGE_M = 8;

/** Centre of an Aabb, for range checks against a board. */
export function aabbCenter(box: Aabb): { x: number; z: number } {
  return { x: (box.minX + box.maxX) / 2, z: (box.minZ + box.maxZ) / 2 };
}

/**
 * The service bay: the Mechanic's place (trades.md §20 — "the workshop
 * bench as the place", adapted to the machines it serves: hulls stand in
 * the world and cannot walk through the workshop's door, so the bay is a
 * marked apron behind the market row). A machine must stand on the apron
 * for the deep verbs; the field adjust works anywhere.
 */
export const SERVICE_BAY = { x: 22, z: 18, radius: 9 } as const;

/** Planters/crates for cover and feel, kept off the walking line. */
export const SETTLEMENT_PLANTERS: readonly Aabb[] = [
  { minX: -6, maxX: -4.6, minZ: 5.2, maxZ: 6.6 },
  { minX: 9, maxX: 10.6, minZ: 5.4, maxZ: 6.8 },
  { minX: -22, maxX: -20.8, minZ: -6.8, maxZ: -5.6 },
];

function streetDoorZone(x: number): Aabb {
  return {
    minX: x - 1.3,
    maxX: x + 1.3,
    minZ: -SETTLEMENT.halfDepth,
    maxZ: -SETTLEMENT.halfDepth + 0.9,
  };
}

/** Standing just outside a club's door in the settlement, facing south. */
function streetArrival(x: number): { x: number; z: number; yaw: number } {
  return { x, z: -SETTLEMENT.halfDepth + 2.2, yaw: 0 };
}

/** A club's own exit door: centred on its south wall. */
function clubExitZone(halfDepth: number): Aabb {
  return { minX: -1.5, maxX: 1.5, minZ: halfDepth - 0.7, maxZ: halfDepth };
}

/** Standing just inside a club, back to the door, facing the booth. */
function clubArrival(halfDepth: number): { x: number; z: number; yaw: number } {
  return { x: 0, z: halfDepth - 2.0, yaw: Math.PI };
}

// ---------------------------------------------------------------------------

const surfacePlan: AreaPlan = {
  id: SURFACE_ID,
  label: "The Surface",
  kind: "surface",
  world: "frontier",
  wilds: true,
  halfWidth: PLANET_HALF,
  halfDepth: PLANET_HALF,
  wallHeight: SETTLEMENT.wallHeight,
  obstacles: [
    ...SETTLEMENT_PLANTERS,
    POSTER_WALL,
    MARKET_BOARD,
    JOB_BOARD,
    ...STREET_DOORS.map(({ x }) => facadeFootprint(x)),
    ...BACK_ROW.map((block) => block.box),
  ],
  portals: STREET_DOORS.map(({ target, x, insideHalfDepth }) => ({
    id: `door-${target}`,
    zone: streetDoorZone(x),
    target,
    arrival: clubArrival(insideHalfDepth),
    label: target,
  })),
  spawn: { x: -25, z: 2.5, radius: 2, yaw: Math.PI / 2 },
  chatRadius: 20,
  accent: 0xd8b04a,
};

const clubZeroPlan: AreaPlan = {
  id: CLUB_ZERO_ID,
  label: "Club Zero",
  kind: "club",
  world: "frontier",
  halfWidth: CLUB_ZERO_HALF_W,
  halfDepth: CLUB_ZERO_HALF_D,
  wallHeight: 4,
  obstacles: [CLUB_ZERO_BOOTH, ...CLUB_ZERO_SPEAKERS],
  portals: [
    {
      id: "exit",
      zone: clubExitZone(CLUB_ZERO_HALF_D),
      target: SURFACE_ID,
      arrival: streetArrival(-14),
      label: "surface",
    },
  ],
  spawn: { x: 0, z: 4, radius: 2.5, yaw: Math.PI },
  mount: CLUB_ZERO_ID,
  booth: CLUB_ZERO_BOOTH,
  // Just inside the door, on the way out — where you actually pick up a
  // flyer on your way home.
  flyerStack: { x: -3.4, z: CLUB_ZERO_HALF_D - 1.6 },
  accent: 0xd8b04a,
};

/** Dressed-shell club: same machinery, smaller room, its own mount. The
 *  exit is a parameter because clubs now stand on more streets than one. */
function shellPlan(
  id: string,
  label: string,
  accent: number,
  exit: { target: string; arrival: { x: number; z: number; yaw: number }; world?: WorldId },
): AreaPlan {
  const halfW = 9;
  const halfD = 7;
  const booth: Aabb = { minX: -1.8, maxX: 1.8, minZ: -halfD, maxZ: -halfD + 1.8 };
  return {
    id,
    label,
    kind: "club",
    world: exit.world,
    halfWidth: halfW,
    halfDepth: halfD,
    wallHeight: 3.6,
    obstacles: [booth],
    portals: [
      {
        id: "exit",
        zone: clubExitZone(halfD),
        target: exit.target,
        arrival: exit.arrival,
        label: exit.target,
      },
    ],
    spawn: { x: 0, z: 3, radius: 2, yaw: Math.PI },
    mount: id,
    booth,
    flyerStack: { x: -2.8, z: halfD - 1.5 },
    accent,
  };
}

/**
 * The workshop: one long bench against the back wall and nothing else.
 * No mount, no booth, nothing to claim — the first room in the world that
 * exists to make something rather than to watch something.
 */
const WORKSHOP_BENCH: Aabb = {
  minX: -4.5,
  maxX: 4.5,
  minZ: -WORKSHOP_HALF_D,
  maxZ: -WORKSHOP_HALF_D + 1.6,
};

/**
 * Crafting v2's bench families, as furniture: four more stations against
 * the side walls. They are dressing and provenance values — the craft gate
 * stays "you are in the workshop" (crafting.md decision 6; v2 decision 17)
 * — but the room should LOOK like four trades happen in it, because it is
 * the only maker's room in the world and it earns its floor space.
 */
export const WORKSHOP_STATIONS: readonly { id: string; label: string; box: Aabb }[] = [
  {
    id: "luthier",
    label: "The Luthier's Rack",
    box: { minX: -WORKSHOP_HALF_W, maxX: -WORKSHOP_HALF_W + 1.3, minZ: -3.4, maxZ: -0.8 },
  },
  {
    id: "tailor",
    label: "The Tailor's Table",
    box: { minX: WORKSHOP_HALF_W - 1.3, maxX: WORKSHOP_HALF_W, minZ: -3.4, maxZ: -0.8 },
  },
  {
    id: "joinery",
    label: "The Joiner's Bench",
    box: { minX: -WORKSHOP_HALF_W, maxX: -WORKSHOP_HALF_W + 1.3, minZ: 0.6, maxZ: 3.2 },
  },
  {
    id: "still",
    label: "The Copper Still",
    box: { minX: WORKSHOP_HALF_W - 1.3, maxX: WORKSHOP_HALF_W, minZ: 0.6, maxZ: 3.2 },
  },
];

const workshopPlan: AreaPlan = {
  id: WORKSHOP_ID,
  label: "The Workshop",
  kind: "workshop",
  world: "frontier",
  halfWidth: WORKSHOP_HALF_W,
  halfDepth: WORKSHOP_HALF_D,
  wallHeight: 3.6,
  obstacles: [WORKSHOP_BENCH, ...WORKSHOP_STATIONS.map((s) => s.box)],
  portals: [
    {
      id: "exit",
      zone: clubExitZone(WORKSHOP_HALF_D),
      target: SURFACE_ID,
      arrival: streetArrival(WORKSHOP_STREET_X),
      label: "surface",
    },
  ],
  // Well inside the room rather than backed against the door: the
  // third-person camera sits a few metres behind you, and a spawn hard
  // against the south wall puts it outside the box looking at nothing.
  spawn: { x: 0, z: 1.2, radius: 1.4, yaw: Math.PI },
  bench: WORKSHOP_BENCH,
  stations: WORKSHOP_STATIONS,
  accent: 0xc98b3a,
};

/**
 * The infirmary: cots along the back wall and nothing else in it. The
 * room does the work — it sheds what the Edge put into you faster than
 * anywhere else, and faster again when somebody is in there with you.
 */
const CLINIC_COTS: Aabb = {
  minX: -CLINIC_HALF_W + 1,
  maxX: CLINIC_HALF_W - 1,
  minZ: -CLINIC_HALF_D,
  maxZ: -CLINIC_HALF_D + 1.8,
};

const clinicPlan: AreaPlan = {
  id: CLINIC_ID,
  label: "The Infirmary",
  kind: "clinic",
  world: "frontier",
  halfWidth: CLINIC_HALF_W,
  halfDepth: CLINIC_HALF_D,
  wallHeight: 3.4,
  obstacles: [CLINIC_COTS],
  portals: [
    {
      id: "exit",
      zone: clubExitZone(CLINIC_HALF_D),
      target: SURFACE_ID,
      arrival: streetArrival(CLINIC_STREET_X),
      label: "surface",
    },
  ],
  // Well inside the room, for the same camera reason the workshop spawn
  // is: a spawn hard against the south wall puts the third-person camera
  // outside the box looking at nothing.
  spawn: { x: 0, z: 1.4, radius: 1.4, yaw: Math.PI },
  accent: 0x7fc8b0,
};

/**
 * The Black's floor plan. Bounds are generous (a 24 km flight box over
 * the planet render); no obstacles, no portals — the way in is an ascent
 * and the way out is a descent, both server-decided. Chat is deliberately
 * room-wide: space is sparse, and hearing another pilot on the comms is
 * the point of there being other pilots.
 */
const spacePlan: AreaPlan = {
  id: SPACE_ID,
  label: "The Black",
  kind: "space",
  halfWidth: 12000,
  halfDepth: 12000,
  wallHeight: 0,
  obstacles: [],
  portals: [],
  spawn: { x: 0, z: 0, radius: 40, yaw: 0 },
  accent: 0x62d6c4,
};

// ---------------------------------------------------------------------------
// Meridian's floor plans.

/** Solid footprint of a Prom-row building (north row faces +z). */
function promFootprint(x: number, width: number, north: boolean): Aabb {
  return north
    ? {
        minX: x - width / 2,
        maxX: x + width / 2,
        minZ: -PROM.halfDepth - PROM_ROW_DEPTH,
        maxZ: -PROM.halfDepth,
      }
    : {
        minX: x - width / 2,
        maxX: x + width / 2,
        minZ: PROM.halfDepth,
        maxZ: PROM.halfDepth + PROM_ROW_DEPTH,
      };
}

/** The customs house on the apron's edge — the port's one solid block. */
const CITY_CUSTOMS_HOUSE: Aabb = { minX: 196, maxX: 214, minZ: -32, maxZ: -18 };

/** Market stalls and crates kept off the walking line of the Prom. */
export const PROM_CLUTTER: readonly Aabb[] = [
  { minX: -122, maxX: -119.4, minZ: 5.6, maxZ: 7.4 },
  { minX: -48, maxX: -45.6, minZ: -7.6, maxZ: -5.8 },
  { minX: 28, maxX: 30.4, minZ: 5.8, maxZ: 7.6 },
  { minX: 128, maxX: 130.6, minZ: -7.4, maxZ: -5.6 },
];

const meridianPlan: AreaPlan = {
  id: MERIDIAN_ID,
  label: "Meridian",
  kind: "surface",
  world: "meridian",
  // Paved and drained: no wilds. The aetherometer reads nothing here, and
  // the Edge does not accumulate — the capital's comfort is its identity,
  // exactly as the frontier's danger is its own.
  halfWidth: CITY_HALF,
  halfDepth: CITY_HALF,
  wallHeight: SETTLEMENT.wallHeight,
  obstacles: [
    ...PROM_NORTH_ROW.map(({ x, width }) => promFootprint(x, width, true)),
    ...PROM_SOUTH_ROW.map(({ x, width }) => promFootprint(x, width, false)),
    CITY_POSTER_WALL,
    CITY_CUSTOMS_HOUSE,
    ...PROM_CLUTTER,
  ],
  portals: [
    {
      id: "door-counterweight",
      zone: { minX: -61.3, maxX: -58.7, minZ: -PROM.halfDepth, maxZ: -PROM.halfDepth + 0.9 },
      target: COUNTERWEIGHT_ID,
      arrival: clubArrival(7),
      label: COUNTERWEIGHT_ID,
    },
    {
      id: "gate-understreet",
      zone: UNDERSTREET_GATE.zone,
      target: UNDERSTREET_ID,
      arrival: UNDERSTREET_GATE.arrivalBelow,
      label: UNDERSTREET_ID,
    },
  ],
  spawn: { x: -10, z: 4, radius: 2.5, yaw: Math.PI / 2 },
  chatRadius: 20,
  accent: 0xd8b04a,
};

/** Colonnade pillars holding the decks up over the Understreet. */
export const UNDERSTREET_PILLARS: readonly Aabb[] = [-96, -34, 34, 96].map((x) => ({
  minX: x - 1.2,
  maxX: x + 1.2,
  minZ: 16.8,
  maxZ: 19.2,
}));

/** Kiosks and greeble on the canyon floor, off the walking line. */
export const UNDERSTREET_CLUTTER: readonly Aabb[] = [
  { minX: -72, maxX: -68.8, minZ: -12.4, maxZ: -9.8 },
  { minX: 8, maxX: 10.6, minZ: 8.2, maxZ: 10.8 },
  { minX: 62, maxX: 64.4, minZ: -10.6, maxZ: -8.4 },
];

/** The hologram's plinth, mid-canyon on the north side. */
export const UNDERSTREET_PLINTH: Aabb = { minX: 38.8, maxX: 41.2, minZ: -14.2, maxZ: -11.8 };

/** The Undertow's door on the canyon's north massing. */
const UNDERTOW_DOOR: Aabb = {
  minX: -14,
  maxX: -10,
  minZ: -UNDERSTREET_HALF_D,
  maxZ: -UNDERSTREET_HALF_D + 0.9,
};

const understreetPlan: AreaPlan = {
  id: UNDERSTREET_ID,
  label: "The Understreet",
  kind: "district",
  world: "meridian",
  halfWidth: UNDERSTREET_HALF_W,
  halfDepth: UNDERSTREET_HALF_D,
  wallHeight: 26,
  obstacles: [...UNDERSTREET_PILLARS, ...UNDERSTREET_CLUTTER, UNDERSTREET_PLINTH],
  portals: [
    {
      id: "gate-meridian",
      zone: { minX: 124, maxX: 131, minZ: -4, maxZ: 4 },
      target: MERIDIAN_ID,
      arrival: UNDERSTREET_GATE.arrivalAbove,
      label: MERIDIAN_ID,
    },
    {
      id: "door-undertow",
      zone: UNDERTOW_DOOR,
      target: UNDERTOW_ID,
      arrival: clubArrival(7),
      label: UNDERTOW_ID,
    },
  ],
  spawn: { x: 108, z: 7, radius: 2, yaw: -Math.PI / 2 },
  chatRadius: 20,
  accent: 0x2fb3c0,
};

export const PLANS: Record<string, AreaPlan> = {
  [SURFACE_ID]: surfacePlan,
  [SPACE_ID]: spacePlan,
  [CLUB_ZERO_ID]: clubZeroPlan,
  [WORKSHOP_ID]: workshopPlan,
  [CLINIC_ID]: clinicPlan,
  "velvet-orbit": shellPlan("velvet-orbit", "The Velvet Orbit", 0x8f5bd6, {
    target: SURFACE_ID,
    arrival: streetArrival(2),
    world: "frontier",
  }),
  "brass-lantern": shellPlan("brass-lantern", "The Brass Lantern", 0xe8913a, {
    target: SURFACE_ID,
    arrival: streetArrival(18),
    world: "frontier",
  }),
  [MERIDIAN_ID]: meridianPlan,
  [UNDERSTREET_ID]: understreetPlan,
  [COUNTERWEIGHT_ID]: shellPlan("counterweight", "The Counterweight", 0xd8a03c, {
    target: MERIDIAN_ID,
    arrival: { x: -60, z: -PROM.halfDepth + 2.2, yaw: 0 },
    world: "meridian",
  }),
  [UNDERTOW_ID]: shellPlan("undertow", "The Undertow", 0xff3b6b, {
    target: UNDERSTREET_ID,
    arrival: { x: -12, z: -UNDERSTREET_HALF_D + 2.4, yaw: 0 },
    world: "meridian",
  }),
};

export const AREA_IDS = Object.keys(PLANS);

/** The surface room a world's descents land on and its interiors exit to. */
export function surfaceIdFor(world: WorldId): string {
  return world === "meridian" ? MERIDIAN_ID : SURFACE_ID;
}

/** A world's landing ground — where its pad crew put a pilot down. */
export function padFor(world: WorldId): { x: number; z: number; radius: number } {
  return world === "meridian" ? CITY_PAD : SPACEPORT_PAD;
}

/**
 * Is this a real area id? Own-property only: PLANS is a plain object, so a
 * bare `PLANS[id]` test also answers yes for every Object.prototype member
 * ("__proto__", "constructor", "toString", …). Any code path that feeds
 * user input to planFor must go through this.
 */
export function isAreaId(roomId: string): boolean {
  return Object.hasOwn(PLANS, roomId);
}

/**
 * The public name of a room. Every panel used to carry its own copy of
 * this map — four of them, drifting independently — while the plans have
 * always known their own labels.
 */
export function areaLabel(roomId: string): string {
  return isAreaId(roomId) ? (PLANS[roomId] as AreaPlan).label : roomId;
}

export function planFor(roomId: string): AreaPlan {
  if (!isAreaId(roomId)) throw new Error(`unknown area: ${roomId}`);
  return PLANS[roomId] as AreaPlan;
}

/** Where a club's music appears to come from: booth front, head height. */
export function boothAudioPosition(plan: AreaPlan): { x: number; y: number; z: number } {
  const booth = plan.booth;
  if (!booth) return { x: 0, y: 1.6, z: 0 };
  return { x: (booth.minX + booth.maxX) / 2, y: 1.6, z: booth.maxZ };
}

/** Does a player circle at (x, z) overlap this box? (portal triggers) */
export function circleOverlapsAabb(x: number, z: number, box: Aabb, radius: number): boolean {
  const cx = Math.max(box.minX, Math.min(x, box.maxX));
  const cz = Math.max(box.minZ, Math.min(z, box.maxZ));
  const dx = x - cx;
  const dz = z - cz;
  return dx * dx + dz * dz < radius * radius;
}
